import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../hooks/useAuth";
import { isNonAdminSalesRep } from "./isNonAdminSalesRep";

// Block non-admin sales reps from a route by redirecting them to the
// configured target (e.g. /salesDashboard). Mirrors withProviderOnlyGuard
// in shape and intent: route HOC + useEffect-based redirect + hydration
// guard. Lives in utils/ rather than inline in Routes.js so unit tests can
// import it without dragging in every guarded page (and their transitive
// `heic-to` ESM parsing pain — see memory note heic-to-jest-esm-workaround).
//
// Designed to compose with other guards; the Peptides route uses it as:
//   withoutNonAdminSalesRepGuard("/salesDashboard", withPeptidesGuard(Peptides))
// so the role check fires before the product-line check.
export function withoutNonAdminSalesRepGuard(redirectTo, Component) {
  return function NonAdminSalesRepGuard(props) {
    const { auth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      // Wait for auth.user to hydrate. Partial state would otherwise
      // wrongly redirect a real provider session during boot.
      if (!auth?.user) return;
      if (isNonAdminSalesRep(auth)) {
        navigate(redirectTo, { replace: true });
      }
    }, [auth, navigate]);

    // Known-blocked: don't mount the guarded page for the one frame before the
    // redirect effect fires — mounting it kicks off its data fetches and shows
    // a flash of content the rep isn't allowed to see. Hydration (no auth.user
    // yet) still renders as before, so legit users don't boot to a blank frame.
    if (auth?.user && isNonAdminSalesRep(auth)) return null;

    return <Component {...props} />;
  };
}
